import { create } from "zustand";
import { persist } from "zustand/middleware";
import CryptoJS from "crypto-js";
import type { UserType } from "../permissions";

export type TProfileAccessLevel = "VIEW" | "EDIT";

export type TProfileAccess = {
  profilePublicId: string;
  name: string;
  accessLevel: TProfileAccessLevel;
};

export type TUserMock = {
  name: string;
  token: string;
  userType: UserType;
  businessProfiles: string[];
  profileAccesses?: TProfileAccess[];
  // Nivel de acesso (VIEW/EDIT) do perfil selecionado em /select-profile.
  selectedProfileAccessLevel?: TProfileAccessLevel;
};

interface UserStore {
  user: TUserMock | null; // Permitir que o usuário seja nulo
  setUser: (user: TUserMock) => void;
  profiles: string[];
  setProfiles: (profilesList: string[]) => void;
  profileAccesses: TProfileAccess[];
  setProfileAccesses: (accesses: TProfileAccess[]) => void;
  clearUser: () => void;
}

const SECRET_KEY = import.meta.env.VITE_APP_HASH_KEY;

const encrypt = (data: unknown) =>
  CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
const decrypt = (data: string) =>
  JSON.parse(
    CryptoJS.AES.decrypt(data, SECRET_KEY).toString(CryptoJS.enc.Utf8)
  );

/**
 * Cliente com acesso VIEW no perfil selecionado nao pode editar.
 * Usuarios internos e clientes legados (sem accessLevel) mantem edicao.
 */
export const canEditSelectedProfile = (user: TUserMock | null): boolean => {
  if (!user) return false;
  if (user.userType !== "CUSTOMER") return true;
  return user.selectedProfileAccessLevel !== "VIEW";
};

export const useUserStore = create<UserStore>()(
  persist(
    (set) => ({
      user: null, // Estado inicial nulo
      profiles: [],
      profileAccesses: [],
      setUser: (user: TUserMock) => set({ user }),
      setProfiles: (profilesList: string[]) => set({ profiles: profilesList }),
      setProfileAccesses: (accesses: TProfileAccess[]) =>
        set({ profileAccesses: accesses }),
      clearUser: () => set({ user: null, profiles: [], profileAccesses: [] }),
    }),
    {
      name: "user-storage",
      storage: {
        getItem: (name) => {
          const v = localStorage.getItem(name);
          if (!v) return null;
          try {
            return decrypt(v);
          } catch (e) {
            return null;
          }
        },
        setItem: (name, value) => localStorage.setItem(name, encrypt(value)),
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);

export default useUserStore;
